import { useAuth } from '@nimbitlabs/auth';

export type Permission = 
  | 'services:read'
  | 'services:create'
  | 'services:update'
  | 'services:delete'
  | 'teams:read'
  | 'teams:create'
  | 'teams:update'
  | 'teams:delete'
  | 'users:read'
  | 'users:create'
  | 'users:update'
  | 'users:delete'
  | 'environments:read'
  | 'environments:create'
  | 'environments:update'
  | 'environments:delete'
  | 'alerts:read'
  | 'alerts:create'
  | 'alerts:update'
  | 'alerts:delete'
  | 'incidents:read'
  | 'incidents:create'
  | 'incidents:update'
  | 'incidents:delete'
  | 'runbooks:read'
  | 'runbooks:create'
  | 'runbooks:update'
  | 'runbooks:delete'
  | 'deployments:read'
  | 'deployments:create'
  | 'dependencies:read'
  | 'monitoring:read'
  | 'audit:read'
  | 'settings:read'
  | 'settings:update'
  | 'api_keys:read'
  | 'api_keys:create'
  | 'api_keys:delete'
  | 'admin';

const rolePermissions: Record<string, Permission[]> = {
  Admin: [
    'services:read', 'services:create', 'services:update', 'services:delete',
    'teams:read', 'teams:create', 'teams:update', 'teams:delete',
    'users:read', 'users:create', 'users:update', 'users:delete',
    'environments:read', 'environments:create', 'environments:update', 'environments:delete',
    'alerts:read', 'alerts:create', 'alerts:update', 'alerts:delete',
    'incidents:read', 'incidents:create', 'incidents:update', 'incidents:delete',
    'runbooks:read', 'runbooks:create', 'runbooks:update', 'runbooks:delete',
    'deployments:read', 'deployments:create',
    'dependencies:read',
    'monitoring:read',
    'audit:read',
    'settings:read', 'settings:update',
    'api_keys:read', 'api_keys:create', 'api_keys:delete',
    'admin'
  ],
  Editor: [
    'services:read', 'services:create', 'services:update',
    'teams:read', 'teams:create', 'teams:update',
    'users:read',
    'environments:read', 'environments:create', 'environments:update',
    'alerts:read', 'alerts:create', 'alerts:update',
    'incidents:read', 'incidents:create', 'incidents:update',
    'runbooks:read', 'runbooks:create', 'runbooks:update',
    'deployments:read',
    'dependencies:read',
    'monitoring:read',
    'audit:read',
    'settings:read',
    'api_keys:read',
  ],
  Viewer: [
    'services:read',
    'teams:read',
    'users:read',
    'environments:read',
    'alerts:read',
    'incidents:read',
    'runbooks:read',
    'deployments:read',
    'dependencies:read',
    'audit:read',
    'settings:read',
  ],
};

export function usePermissions() {
  const { user } = useAuth();
  
  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    
    // Default to Admin role if not specified (for development/testing)
    // In production, this should come from the backend
    const userRole = user.role || 'admin';
    
    // Normalize role to match rolePermissions keys (capitalize first letter)
    const normalizedRole = userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase();
    const permissions = rolePermissions[normalizedRole] || [];
    
    return permissions.includes(permission);
  };
  
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(p => hasPermission(p));
  };
  
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(p => hasPermission(p));
  };
  
  const isAdmin = (): boolean => {
    return hasPermission('admin');
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
  };
}
